import { useNavigate, Link } from "react-router-dom";
import "@/App.css";
import SEO from "../components/shared/SEO";
import { 
  ArrowLeft,
  ArrowRight,
  BookOpen,
  DollarSign,
  Zap,
  Mail,
  Linkedin,
  Users,
  Award,
  Search,
  Target,
  FileText,
  MessageSquare,
  RefreshCw,
  ChevronRight,
  Download,
  Play,
  Workflow,
  CheckCircle2,
  Map
} from "lucide-react";

// The Multiplier Brand Colors
const BRAND = {
  gold: "#D4AF37",
  goldLight: "#E8C547",
  charcoal: "#1C1C1C",
  surface: "#0A0A0A",
  text: "#FAFAFA"
};

// Header Component
const Header = () => {
  const navigate = useNavigate();
  
  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/90 backdrop-blur-xl border-b border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="flex items-center justify-between h-16 sm:h-20">
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 sm:gap-3 hover:opacity-80 transition-opacity"
          >
            <svg width="28" height="28" viewBox="0 0 64 64" fill="none" className="sm:w-9 sm:h-9">
              <rect x="6" y="46" width="11" height="14" fill={BRAND.gold} opacity="0.5" rx="2"/>
              <rect x="20" y="36" width="11" height="24" fill={BRAND.gold} opacity="0.65" rx="2"/>
              <rect x="34" y="24" width="11" height="36" fill={BRAND.gold} opacity="0.8" rx="2"/>
              <rect x="48" y="10" width="11" height="50" fill={BRAND.gold} rx="2"/>
            </svg>
            <div className="flex flex-col">
              <span className="text-base sm:text-lg font-bold tracking-tight text-white leading-none">LRS</span>
              <span className="text-[8px] sm:text-[10px] text-zinc-500 tracking-widest uppercase hidden sm:block">Leitner Revenue Systems</span>
            </div>
          </button>
          
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-1.5 sm:gap-2 text-zinc-400 hover:text-white transition-colors text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Back to Home</span>
            <span className="sm:hidden">Back</span>
          </button>
        </div>
      </div>
    </header>
  );
};

// Resource Card Component
const ResourceCard = ({ title, subtitle, description, icon: Icon, href, pdfHref, image, isNew, isExternal }) => {
  const navigate = useNavigate();
  
  const handleClick = () => {
    if (isExternal) {
      window.location.href = href;
    } else {
      navigate(href);
    }
  };
  
  return (
    <div className="group bg-[#1C1C1C] rounded-2xl overflow-hidden border border-white/5 hover:border-[#D4AF37]/30 transition-all">
      {/* Image */}
      <div className="relative aspect-video overflow-hidden">
        <img 
          src={image}
          alt={title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1C1C1C] via-transparent to-transparent" />
        {isNew && (
          <div className="absolute top-4 right-4 px-3 py-1 bg-[#D4AF37] rounded-full">
            <span className="text-xs font-bold text-[#0A0A0A]">NEW</span>
          </div>
        )}
        <div className="absolute bottom-4 left-4">
          <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/20 backdrop-blur-xl flex items-center justify-center">
            <Icon className="w-6 h-6 text-[#D4AF37]" />
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="p-6">
        <div className="text-sm text-[#D4AF37] font-medium mb-2">{subtitle}</div>
        <h3 className="text-xl font-bold text-white mb-3">{title}</h3>
        <p className="text-zinc-400 mb-6 line-clamp-3">{description}</p>
        
        <div className="flex flex-wrap gap-3">
          <button 
            onClick={handleClick}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#D4AF37] text-[#0A0A0A] rounded-lg font-semibold hover:bg-[#E8C547] transition-all text-sm"
          >
            {isExternal ? "Launch Playbook" : "Read Online"}
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

// Main Resources Page
function ResourcesPage() {
  const navigate = useNavigate();
  
  const resources = [
    {
      title: "Account Mapping Playbook",
      subtitle: "Interactive Workflow",
      description: "An interactive walkthrough of how to run the account mapping workflow—from research and signals to buyer mapping, need hypothesis, and product value. Built to sharpen judgment, not replace it.",
      icon: Map,
      href: "/account-mapping-playbook/index.html",
      isExternal: true,
      image: "https://customer-assets.emergentagent.com/job_efficiency-boost-10/artifacts/hbdoli1e_ChatGPT%20Image%20May%2023%2C%202026%2C%2003_47_17%20PM.png",
      isNew: true
    },
    {
      title: "AI-Powered SDR Team Design Guidebook",
      subtitle: "Executive Resource",
      description: "Designing Systems That Scale Without Losing the Human Edge. Learn how the highest-performing teams treat AI as infrastructure, not a shortcut. Covers system design frameworks, signal vs volume, and building feedback loops.",
      icon: Zap,
      href: "/resources/ai-sdr-guidebook",
      pdfHref: "/AI-SDR-Guidebook.pdf",
      // Using the same image as homepage
      image: "https://media.licdn.com/dms/image/v2/D5612AQHoqD9j6OPvfQ/article-cover_image-shrink_720_1280/B56ZuaiYovKEAI-/0/1767824267682?e=2147483647&v=beta&t=ruFDrcPhUkjhXWmtJ0Syx8_TeZX8JTAYko63S-061Lw",
      isNew: false
    },
    {
      title: "SDR Economics: Executive Measurement Playbook",
      subtitle: "Executive Resource",
      description: "How High-Performing Sales and RevOps Leaders Actually Measure SDR ROI. A clear, practical framework covering fully loaded costs, payback periods, LTV calculations, and the impact of tenure on ROI.",
      icon: DollarSign,
      href: "/resources/sdr-economics",
      pdfHref: "/SDR-Economics-Playbook.pdf",
      // Using the same image as homepage
      image: "https://media.licdn.com/dms/image/v2/D4D12AQGGRGuMGAL3mA/article-cover_image-shrink_720_1280/B4DZxTkMADHkAI-/0/1770928527443?e=2147483647&v=beta&t=-2FnyaulHj-TRfafbL3WELljIJJ6RvXNlkv14hi9tLM",
      isNew: false
    },
    {
      title: "Building Talent Pools for High-Growth SaaS Sales Teams",
      subtitle: "Executive Guide",
      description: "Scaling from 10 to 100+ reps requires a systematic talent pool strategy. Learn the three pillars of talent pools, implementation roadmaps, key metrics, and how to avoid common pitfalls in hypergrowth hiring.",
      icon: Users,
      href: "/resources/talent-pool",
      pdfHref: "/Talent-Pool-Guide.pdf",
      // Using the same image as homepage
      image: "https://media.licdn.com/dms/image/v2/D5612AQGWYSSnqjD04Q/article-cover_image-shrink_720_1280/B56Zza7McgH8AI-/0/1773199482538?e=2147483647&v=beta&t=EdThB6PbZGoiEiUeYzUNigz3957oIopIj_ofVkBKV7k",
      isNew: false
    }
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <SEO 
        title="Resources | Playbooks, Tools & Guides"
        description="Free executive resources and playbooks for sales and revenue leaders building modern, AI-enabled revenue systems. AI SDR Playbooks, SDR Economics, and more."
        path="/resources"
        image="resources"
      />
      <Header />
      
      {/* Hero */}
      <section className="pt-24 sm:pt-32 pb-12 sm:pb-16 px-4 sm:px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-full mb-6">
              <BookOpen className="w-4 h-4 text-[#D4AF37]" />
              <span className="text-sm font-medium text-[#D4AF37]">Free Resources</span>
            </div>
            
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-tight mb-6">
              Executive Resources &<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#D4AF37] to-[#E8C547]">
                Playbooks
              </span>
            </h1>
            
            <p className="text-base sm:text-xl text-zinc-400 leading-relaxed">
              Practical frameworks and playbooks for sales and revenue leaders building modern, AI-enabled revenue systems.
            </p>
          </div>
        </div>
      </section>

      {/* Featured Playbook - AI SDR Training System */}
      <section className="py-6 sm:py-8 px-4 sm:px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="bg-gradient-to-br from-[#1C1C1C] via-[#1C1C1C] to-[#D4AF37]/10 rounded-2xl sm:rounded-3xl border border-[#D4AF37]/30 overflow-hidden">
            <div className="grid lg:grid-cols-5 gap-0">
              {/* Left Side - System Visual (60%) */}
              <div className="lg:col-span-3 p-5 sm:p-8 lg:p-12">
                {/* Badge */}
                <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#D4AF37]/20 border border-[#D4AF37]/30 rounded-full mb-4 sm:mb-6">
                  <Award className="w-4 h-4 text-[#D4AF37]" />
                  <span className="text-xs font-bold text-[#D4AF37]">FEATURED SYSTEM PLAYBOOK</span>
                </div>
                
                <h2 className="text-xl sm:text-2xl lg:text-4xl font-bold text-white mb-2 sm:mb-3 tracking-tight">
                  The LRS AI SDR Training Playbook
                </h2>
                
                <p className="text-base sm:text-lg text-zinc-300 mb-4 sm:mb-6">
                  How to Build AI SDR Workflows That Actually Perform
                </p>
                
                <div className="mb-6 sm:mb-8">
                  <p className="text-zinc-400 mb-2 text-sm sm:text-base">
                    <span className="text-white font-medium">Most teams fail with AI SDRs</span> because they try to build one agent to do everything.
                  </p>
                  <p className="text-[#D4AF37] font-semibold text-sm sm:text-base">
                    Top teams build systems of specialized workflows.
                  </p>
                </div>

                {/* Interactive 6-Stage System Preview */}
                <div className="grid grid-cols-3 gap-3 mb-6">
                  {[
                    { num: 1, title: "Prospecting", icon: Search, color: "blue" },
                    { num: 2, title: "Research", icon: Target, color: "purple" },
                    { num: 3, title: "Content", icon: FileText, color: "orange" },
                    { num: 4, title: "Outreach", icon: Mail, color: "green" },
                    { num: 5, title: "Follow-Up", icon: MessageSquare, color: "cyan" },
                    { num: 6, title: "System", icon: RefreshCw, color: "gold" }
                  ].map((stage) => {
                    const Icon = stage.icon;
                    const colorMap = {
                      blue: "bg-blue-500/20 text-blue-400 border-blue-500/30",
                      purple: "bg-purple-500/20 text-purple-400 border-purple-500/30",
                      orange: "bg-orange-500/20 text-orange-400 border-orange-500/30",
                      green: "bg-green-500/20 text-green-400 border-green-500/30",
                      cyan: "bg-cyan-500/20 text-cyan-400 border-cyan-500/30",
                      gold: "bg-[#D4AF37]/20 text-[#D4AF37] border-[#D4AF37]/30"
                    };
                    return (
                      <div 
                        key={stage.num}
                        className={`p-3 rounded-xl border ${colorMap[stage.color]} text-center transition-all hover:scale-105 cursor-pointer`}
                        onClick={() => navigate('/resources/ai-sdr-playbook')}
                      >
                        <Icon className="w-5 h-5 mx-auto mb-1.5" />
                        <div className="text-[10px] font-bold opacity-60">{stage.num}</div>
                        <div className="text-xs font-medium text-white">{stage.title}</div>
                      </div>
                    );
                  })}
                </div>

                {/* Flow indicator */}
                <div className="flex items-center justify-center gap-1 text-zinc-500 text-xs">
                  <span>Flow:</span>
                  {[1,2,3,4,5,6].map((n, i) => (
                    <span key={n} className="flex items-center">
                      <span className="text-[#D4AF37]">{n}</span>
                      {i < 5 && <ChevronRight className="w-3 h-3" />}
                    </span>
                  ))}
                  <RefreshCw className="w-3 h-3 text-[#D4AF37] ml-1" />
                </div>
              </div>

              {/* Right Side - CTA Panel (40%) */}
              <div className="lg:col-span-2 bg-[#0A0A0A]/50 p-8 sm:p-10 lg:p-12 flex flex-col justify-center border-t lg:border-t-0 lg:border-l border-white/5">
                <h3 className="text-lg font-semibold text-white mb-4">What's Inside</h3>
                
                <p className="text-sm text-zinc-400 mb-6">
                  This playbook breaks down how high-performing teams design, train, and operate AI SDR systems across the entire outbound funnel.
                </p>
                
                <ul className="space-y-3 mb-8">
                  <li className="flex items-center gap-3 text-sm text-zinc-300">
                    <div className="w-6 h-6 rounded-full bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0">
                      <Workflow className="w-3.5 h-3.5 text-[#D4AF37]" />
                    </div>
                    6 core AI SDR workflows
                  </li>
                  <li className="flex items-center gap-3 text-sm text-zinc-300">
                    <div className="w-6 h-6 rounded-full bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0">
                      <CheckCircle2 className="w-3.5 h-3.5 text-[#D4AF37]" />
                    </div>
                    Operator checklists per workflow
                  </li>
                  <li className="flex items-center gap-3 text-sm text-zinc-300">
                    <div className="w-6 h-6 rounded-full bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0">
                      <Play className="w-3.5 h-3.5 text-[#D4AF37]" />
                    </div>
                    Real execution model (not theory)
                  </li>
                </ul>
                
                <div className="space-y-3">
                  <button
                    onClick={() => navigate('/resources/ai-sdr-playbook')}
                    className="w-full px-6 py-3.5 bg-[#D4AF37] text-[#0A0A0A] rounded-xl font-semibold hover:bg-[#E8C547] transition-all flex items-center justify-center gap-2 shadow-lg shadow-[#D4AF37]/20"
                  >
                    <BookOpen className="w-5 h-5" />
                    View Full Playbook
                  </button>
                  
                  <button
                    onClick={() => navigate('/resources/ai-sdr-playbook#download')}
                    className="w-full px-6 py-3.5 bg-transparent border border-[#D4AF37]/50 text-[#D4AF37] rounded-xl font-semibold hover:bg-[#D4AF37]/10 transition-all flex items-center justify-center gap-2"
                  >
                    <Download className="w-5 h-5" />
                    Download PDF
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Supporting Resources Header */}
      <section className="pt-8 pb-4 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <h3 className="text-xl font-semibold text-white mb-2">Supporting Resources</h3>
          <p className="text-zinc-400 text-sm">Complementary guides for building your revenue system</p>
        </div>
      </section>

      {/* Resources Grid */}
      <section className="py-16 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            {resources.map((resource) => (
              <ResourceCard key={resource.title} {...resource} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 lg:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <div className="p-12 bg-gradient-to-r from-[#1C1C1C] to-[#1C1C1C]/50 rounded-3xl border border-[#D4AF37]/20">
            <h2 className="text-3xl font-bold text-white mb-4">
              Need Help Implementing These Frameworks?
            </h2>
            <p className="text-lg text-zinc-400 mb-8 max-w-2xl mx-auto">
              Schedule a consultation to discuss how these principles apply to your specific revenue challenges.
            </p>
            <a 
              href="https://calendly.com/leitner-revenue-systems/30min"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#D4AF37] text-[#0A0A0A] rounded-lg font-semibold hover:bg-[#E8C547] transition-all"
            >
              Schedule Consultation
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 lg:px-12 bg-[#0A0A0A] border-t border-white/5">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <svg width="32" height="32" viewBox="0 0 64 64" fill="none">
              <rect x="6" y="46" width="11" height="14" fill={BRAND.gold} opacity="0.5" rx="2"/>
              <rect x="20" y="36" width="11" height="24" fill={BRAND.gold} opacity="0.65" rx="2"/>
              <rect x="34" y="24" width="11" height="36" fill={BRAND.gold} opacity="0.8" rx="2"/>
              <rect x="48" y="10" width="11" height="50" fill={BRAND.gold} rx="2"/>
            </svg>
            <span className="text-zinc-500 text-sm">© 2026 Leitner Revenue Systems, LLC</span>
          </div>
          <div className="flex items-center gap-6">
            <a 
              href="https://www.linkedin.com/company/leitner-revenue-systems-llc/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-zinc-500 hover:text-[#D4AF37] transition-colors"
            >
              <Linkedin className="w-5 h-5" />
            </a>
            <a 
              href="mailto:Dan@LRSrevenue.com"
              className="text-zinc-500 hover:text-[#D4AF37] transition-colors"
            >
              <Mail className="w-5 h-5" />
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default ResourcesPage;
